import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title = "SOLARIS | Cinematic LUTs for Film & Video Color Grading",
  description = "Professional cinematic LUTs inspired by iconic films. 20 premium color grades for Sony S-Log, ARRI LogC, RED IPP2, Canon C-Log, Blackmagic, Panasonic V-Log & Apple Log.",
  keywords = "",
  canonical = "https://solaris.skypanpictures.com/",
  image = "https://solaris.skypanpictures.com/og-image.png",
  type = "website",
  productPrice = null,
  cameraName = null
}) => {
  // Build full keywords string
  const baseKeywords = "cinematic LUTs, film LUTs, color grading LUTs, LUT pack, video LUTs, professional LUTs, DaVinci Resolve LUTs, Premiere Pro LUTs, Final Cut Pro LUTs, film color grading, Hollywood color science, LOG to Rec.709, .cube LUT files, movie look LUTs, Skypan Pictures, SOLARIS LUT pack";
  const fullKeywords = keywords ? `${keywords}, ${baseKeywords}` : baseKeywords;

  // Camera-specific schema if applicable
  const productSchema = productPrice ? {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": title,
    "description": description,
    "image": image,
    "brand": {
      "@type": "Brand",
      "name": "SOLARIS by Skypan Pictures"
    },
    "offers": {
      "@type": "Offer",
      "url": canonical,
      "priceCurrency": "USD",
      "price": productPrice,
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": "Skypan Pictures"
      }
    },
    "category": "Video Editing Software",
    "keywords": fullKeywords
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={fullKeywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Product Schema if applicable */}
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-configured SEO for each page
export const MainPageSEO = () => (
  <SEO 
    title="SOLARIS | Cinematic LUTs for Film & Video Color Grading"
    description="20 professional cinematic LUTs inspired by iconic films. Transform your footage with Hollywood-grade color science. Available for Sony, ARRI, RED, Canon, Blackmagic, Panasonic & Apple cameras. $49 for the complete pack."
    canonical="https://solaris.skypanpictures.com/"
    productPrice="49.00"
  />
);

export const SonyPageSEO = () => (
  <SEO 
    title="Sony S-Log LUTs | SOLARIS Cinematic Color Grading for Sony Cameras"
    description="20 cinematic LUTs optimized for Sony S-Log2 and S-Log3. Compatible with FX3, FX6, FX9, A7S III, A7 IV, A7R V, ZV-E1, FX30. Transform Sony LOG footage with film-inspired color grades. $24."
    keywords="Sony LUTs, S-Log LUTs, S-Log2 LUTs, S-Log3 LUTs, Sony FX3 LUTs, Sony FX6 LUTs, Sony A7S III LUTs, Sony color grading, Sony cinema camera LUTs"
    canonical="https://solaris.skypanpictures.com/sony"
    productPrice="24.00"
    cameraName="Sony"
  />
);

export const ARRIPageSEO = () => (
  <SEO 
    title="ARRI LogC LUTs | SOLARIS Cinematic Color Grading for ARRI Cameras"
    description="20 cinematic LUTs optimized for ARRI LogC. Compatible with ALEXA Mini, ALEXA LF, ALEXA 35, AMIRA. Transform ARRI LOG footage with film-inspired color grades. $24."
    keywords="ARRI LUTs, LogC LUTs, ARRI ALEXA LUTs, ALEXA Mini LUTs, ALEXA LF LUTs, ALEXA 35 LUTs, ARRI color grading, ARRI cinema camera LUTs"
    canonical="https://solaris.skypanpictures.com/arri"
    productPrice="24.00"
    cameraName="ARRI"
  />
);

export const REDPageSEO = () => (
  <SEO 
    title="RED Camera LUTs | SOLARIS Cinematic Color Grading for RED IPP2"
    description="20 cinematic LUTs optimized for RED IPP2 Log3G10 and REDLogFilm. Compatible with KOMODO, V-RAPTOR, DSMC2, RED ONE. Transform RED LOG footage with film-inspired color grades. $24."
    keywords="RED LUTs, IPP2 LUTs, Log3G10 LUTs, REDLogFilm LUTs, RED KOMODO LUTs, V-RAPTOR LUTs, RED color grading, RED cinema camera LUTs"
    canonical="https://solaris.skypanpictures.com/red"
    productPrice="24.00"
    cameraName="RED"
  />
);

export const CanonPageSEO = () => (
  <SEO 
    title="Canon C-Log LUTs | SOLARIS Cinematic Color Grading for Canon Cameras"
    description="20 cinematic LUTs optimized for Canon C-Log, C-Log2, and C-Log3. Compatible with C70, C300 III, C500 II, R5, R5C, R3. Transform Canon LOG footage with film-inspired color grades. $24."
    keywords="Canon LUTs, C-Log LUTs, C-Log2 LUTs, C-Log3 LUTs, Canon C70 LUTs, Canon C300 LUTs, Canon R5 LUTs, Canon color grading, Canon cinema camera LUTs"
    canonical="https://solaris.skypanpictures.com/canon"
    productPrice="24.00"
    cameraName="Canon"
  />
);

export const BlackmagicPageSEO = () => (
  <SEO 
    title="Blackmagic LUTs | SOLARIS Cinematic Color Grading for BMPCC & URSA"
    description="20 cinematic LUTs optimized for Blackmagic BMDFilm. Compatible with BMPCC 4K/6K, URSA Mini Pro, URSA G2. Transform Blackmagic LOG footage with film-inspired color grades. $24."
    keywords="Blackmagic LUTs, BMDFilm LUTs, BMPCC LUTs, BMPCC 4K LUTs, BMPCC 6K LUTs, URSA Mini LUTs, Blackmagic color grading, Blackmagic cinema camera LUTs"
    canonical="https://solaris.skypanpictures.com/blackmagic"
    productPrice="24.00"
    cameraName="Blackmagic"
  />
);

export const PanasonicPageSEO = () => (
  <SEO 
    title="Panasonic V-Log LUTs | SOLARIS Cinematic Color Grading for Lumix & Varicam"
    description="20 cinematic LUTs optimized for Panasonic V-Log and V-Log L. Compatible with S1H, S5 II, GH6, GH5, BGH1, Varicam. Transform Panasonic LOG footage with film-inspired color grades. $24."
    keywords="Panasonic LUTs, V-Log LUTs, V-Log L LUTs, Lumix LUTs, S1H LUTs, S5 LUTs, GH6 LUTs, GH5 LUTs, Panasonic color grading, Panasonic cinema camera LUTs"
    canonical="https://solaris.skypanpictures.com/panasonic"
    productPrice="24.00"
    cameraName="Panasonic"
  />
);

export const ApplePageSEO = () => (
  <SEO 
    title="Apple Log LUTs | SOLARIS Cinematic Color Grading for iPhone & Apple ProRes"
    description="20 cinematic LUTs optimized for Apple Log. Compatible with iPhone 15 Pro, iPhone 15 Pro Max. Transform Apple LOG footage with film-inspired color grades. $24."
    keywords="Apple Log LUTs, iPhone LUTs, iPhone 15 Pro LUTs, Apple ProRes LUTs, iPhone color grading, Apple Log color grading, mobile filmmaking LUTs"
    canonical="https://solaris.skypanpictures.com/apple"
    productPrice="24.00"
    cameraName="Apple"
  />
);

export const StandardPageSEO = () => (
  <SEO 
    title="Standard Rec.709 LUTs | SOLARIS Cinematic Color Grading for Any Camera"
    description="20 cinematic LUTs for standard Rec.709 and generic LOG footage. Universal compatibility with any camera. Transform your footage with film-inspired color grades. $24."
    keywords="Rec.709 LUTs, standard LUTs, universal LUTs, generic LOG LUTs, any camera LUTs, beginner LUTs, creative LUTs, video color grading"
    canonical="https://solaris.skypanpictures.com/standard"
    productPrice="24.00"
    cameraName="Standard"
  />
);

export const TermsPageSEO = () => (
  <SEO 
    title="Terms of Use & License | SOLARIS by Skypan Pictures"
    description="Terms of use, single-user license agreement, and legal information for SOLARIS Cinematic LUT Library by Skypan Pictures."
    canonical="https://solaris.skypanpictures.com/terms"
    type="article"
  />
);

export default SEO;
